// adminportal/src/pages/WebPortalLogin.tsx
import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, User, Lock } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';
import Copyright from '../components/Copyright';
import Logo from '../components/Logo';
import { useNavigate } from 'react-router-dom';
import { CognitoAuthService } from '../services/cognitoAuth';
import { markAuthed } from '../auth';
import { apiUrl } from '../config';
import { usePageTitle } from '../hooks/usePageTitle';
import { loadCognitoConfig } from '../config/cognito';

export default function WebPortalLogin() {
  const { settings } = useSettings();
  const nav = useNavigate();

  // Set page title
  usePageTitle('Transfer Portal Login');

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');

  // MFA state
  const [mfaSession, setMfaSession] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [showMfaForm, setShowMfaForm] = useState(false);
  const [mfaUsername, setMfaUsername] = useState<string>('');

  // Load Cognito configuration on component mount
  useEffect(() => {
    const initializeCognito = async () => {
      try {
        await loadCognitoConfig();
        console.log('✅ Cognito configuration loaded successfully');
      } catch (error) {
        console.error('❌ Failed to load Cognito configuration:', error);
      }
    };

    initializeCognito();
  }, []);

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    try {
      console.log('🔍 WebPortalLogin Login Debug:');
      console.log('  - Username:', username);
      console.log('  - Password length:', password?.length);

      // Use Cognito authentication
      const result = await CognitoAuthService.login(username, password);

      console.log('🔍 WebPortalLogin Login result:', result);

      if (result.success) {
        // Login successful - always go to web portal first
        markAuthed();
        // Store user type for later use
        localStorage.setItem('userType', result.userType || 'user');
        nav('/web-portal');
      } else if (result.challengeName === 'SOFTWARE_TOKEN_MFA') {
        // MFA challenge required
        console.log('🔍 MFA challenge required, showing MFA form');
        setMfaSession(result.session || null);
        setMfaUsername(username); // Store the username for MFA challenge
        setShowMfaForm(true);
        setErrorMessage('Please enter your MFA code to continue.');
      } else {
        // Login failed - show error message
        setErrorMessage(result.error || 'Login failed. Please check your credentials.');
      }
    } catch (error) {
      console.error('🔍 WebPortalLogin Login Error:', error);
      console.log('🔍 Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        name: error instanceof Error ? error.name : undefined,
      });
      setErrorMessage('An error occurred during login. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleMfaSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!mfaSession || !mfaCode) {
      setErrorMessage('❌ Please enter your MFA code');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      console.log('🔍 WebPortalLogin MFA Debug:');
      console.log('  - Session:', mfaSession ? 'Present' : 'Missing');
      console.log('  - MFA Code length:', mfaCode?.length);
      console.log('  - Username:', mfaUsername);

      const result = await CognitoAuthService.respondToMfaChallenge(
        mfaSession,
        mfaCode,
        mfaUsername
      );

      console.log('🔍 WebPortalLogin MFA result:', result);

      if (result.success) {
        // MFA successful - always go to web portal first
        markAuthed();
        // Store user type for later use
        localStorage.setItem('userType', result.userType || 'user');
        nav('/web-portal');
      } else {
        // MFA failed - show error message
        setErrorMessage(result.error || 'MFA verification failed. Please try again.');
      }
    } catch (error) {
      console.error('🔍 WebPortalLogin MFA Error:', error);
      setErrorMessage('An error occurred during MFA verification. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleForgotPassword(e: React.FormEvent) {
    e.preventDefault();
    if (!forgotEmail) {
      setForgotMessage('❌ Please enter your email address');
      return;
    }

    try {
      const response = await fetch(apiUrl('forgot-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: forgotEmail }),
      });

      const data = await response.json();
      if (response.ok) {
        setForgotMessage('✅ Password reset code sent to your email');
        setForgotEmail('');
      } else {
        setForgotMessage(`❌ ${data.message || 'Failed to send reset code'}`);
      }
    } catch (error) {
      setForgotMessage('❌ An error occurred. Please try again.');
    }
  }

  return (
    <div
      className='min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8'
      style={{ backgroundColor: 'var(--color-bg-primary)' }}
    >
      <div className='max-w-md w-full space-y-8'>
        {/* Header */}
        <div className='text-center'>
          <div className='w-full flex justify-center mb-8'>
            <Logo size='xs' showText={false} />
          </div>
          <h2 className='mt-6 text-3xl font-bold' style={{ color: 'var(--color-text-primary)' }}>
            Web Transfer Portal
          </h2>
          <p className='mt-2 text-sm' style={{ color: 'var(--color-text-secondary)' }}>
            Secure file transfer for your organization
          </p>
        </div>

        {/* Login Form */}
        <form className='mt-8 space-y-6' onSubmit={handleLogin}>
          <div className='space-y-4'>
            {/* Username Field */}
            <div>
              <label
                htmlFor='username'
                className='block text-sm font-medium mb-2'
                style={{ color: 'var(--color-text-secondary)' }}
              >
                Username or Email
              </label>
              <div className='relative'>
                <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                  <User className='h-5 w-5' style={{ color: 'var(--color-text-tertiary)' }} />
                </div>
                <input
                  id='username'
                  name='username'
                  type='text'
                  autoComplete='username'
                  required
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  className='block w-full pl-12 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent'
                  style={{
                    backgroundColor: 'var(--color-input-bg)',
                    borderColor: 'var(--color-input-border)',
                    color: 'var(--color-input-text)',
                  }}
                  placeholder='Enter your username or email'
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label
                htmlFor='password'
                className='block text-sm font-medium mb-2'
                style={{ color: 'var(--color-text-secondary)' }}
              >
                Password
              </label>
              <div className='relative'>
                <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                  <Lock className='h-5 w-5' style={{ color: 'var(--color-text-tertiary)' }} />
                </div>
                <input
                  id='password'
                  name='password'
                  type={showPassword ? 'text' : 'password'}
                  autoComplete='current-password'
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className='block w-full pl-12 pr-12 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent'
                  style={{
                    backgroundColor: 'var(--color-input-bg)',
                    borderColor: 'var(--color-input-border)',
                    color: 'var(--color-input-text)',
                  }}
                  placeholder='Enter your password'
                />
                <button
                  type='button'
                  className='absolute inset-y-0 right-0 pr-3 flex items-center'
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ color: 'var(--color-text-tertiary)' }}
                >
                  {showPassword ? <EyeOff className='h-5 w-5' /> : <Eye className='h-5 w-5' />}
                </button>
              </div>
            </div>
          </div>

          {/* Remember Me & Forgot Password */}
          <div className='flex items-center justify-between'>
            <div className='flex items-center'>
              <input
                id='remember-me'
                name='remember-me'
                type='checkbox'
                checked={remember}
                onChange={e => setRemember(e.target.checked)}
                className='h-4 w-4 rounded'
                style={{
                  backgroundColor: 'var(--color-input-bg)',
                  borderColor: 'var(--color-input-border)',
                  accentColor: 'var(--color-primary)',
                }}
              />
              <label
                htmlFor='remember-me'
                className='ml-2 block text-sm'
                style={{ color: 'var(--color-text-secondary)' }}
              >
                Remember me
              </label>
            </div>

            <button
              type='button'
              onClick={() => setForgotMessage('forgot')}
              className='text-sm transition-colors'
              style={{ color: 'var(--color-primary)' }}
            >
              Forgot password?
            </button>
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div
              className='px-4 py-3 rounded-lg text-sm'
              style={{
                backgroundColor: 'var(--color-error-bg)',
                borderColor: 'var(--color-error-border)',
                color: 'var(--color-error-text)',
                border: '1px solid',
              }}
            >
              {errorMessage}
            </div>
          )}

          {/* Login Button */}
          <button
            type='submit'
            disabled={isLoading}
            className='group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'
            style={{
              backgroundColor: 'var(--color-btn-primary)',
              color: 'var(--color-text-primary)',
            }}
          >
            {isLoading ? (
              <div className='flex items-center'>
                <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2'></div>
                Signing in...
              </div>
            ) : (
              'Sign in to Portal'
            )}
          </button>
        </form>

        {/* MFA Form */}
        {showMfaForm && (
          <div className='mt-6 p-4 bg-slate-800 rounded-lg'>
            <h3 className='text-lg font-medium text-white mb-3'>Multi-Factor Authentication</h3>
            <p className='text-sm text-slate-300 mb-4'>
              Please enter the 6-digit code from your authenticator app.
            </p>
            <form onSubmit={handleMfaSubmit} className='space-y-3'>
              <input
                type='text'
                value={mfaCode}
                onChange={e => setMfaCode(e.target.value)}
                placeholder='Enter 6-digit MFA code'
                maxLength={6}
                className='w-full px-3 py-2 border border-slate-600 rounded-lg bg-slate-700 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-lg tracking-widest'
              />
              <div className='flex space-x-2'>
                <button
                  type='submit'
                  disabled={isLoading || mfaCode.length !== 6}
                  className='flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white py-2 px-4 rounded-lg text-sm font-medium'
                >
                  {isLoading ? 'Verifying...' : 'Verify MFA Code'}
                </button>
                <button
                  type='button'
                  onClick={() => {
                    setShowMfaForm(false);
                    setMfaCode('');
                    setMfaSession(null);
                    setErrorMessage('');
                  }}
                  className='flex-1 bg-slate-600 hover:bg-slate-700 text-white py-2 px-4 rounded-lg text-sm font-medium'
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Forgot Password Form */}
        {forgotMessage === 'forgot' && (
          <div className='mt-6 p-4 bg-slate-800 rounded-lg'>
            <h3 className='text-lg font-medium text-white mb-3'>Reset Password</h3>
            <form onSubmit={handleForgotPassword} className='space-y-3'>
              <input
                type='email'
                value={forgotEmail}
                onChange={e => setForgotEmail(e.target.value)}
                placeholder='Enter your email address'
                className='w-full px-3 py-2 border border-slate-600 rounded-lg bg-slate-700 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500'
              />
              <div className='flex space-x-2'>
                <button
                  type='submit'
                  className='flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg text-sm font-medium'
                >
                  Send Reset Code
                </button>
                <button
                  type='button'
                  onClick={() => setForgotMessage('')}
                  className='flex-1 bg-slate-600 hover:bg-slate-700 text-white py-2 px-4 rounded-lg text-sm font-medium'
                >
                  Cancel
                </button>
              </div>
              {forgotMessage && forgotMessage !== 'forgot' && (
                <p className='text-sm text-slate-300'>{forgotMessage}</p>
              )}
            </form>
          </div>
        )}

        {/* Footer */}
        <div className='text-center'>
          <Copyright />
        </div>
      </div>
    </div>
  );
}
